import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import BrandMark from "../components/BrandMark";
import { SiteFooter } from "../components/Layout";
import { CheckCircle2, AlertCircle } from "lucide-react";

/**
 * `/unsubscribe?u=<id>&l=<list>&t=<token>`
 * Calls the signed unsubscribe endpoint and shows a friendly confirmation.
 */
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true, ok: false, msg: "", email: "", list: "" });

  useEffect(() => {
    const u = params.get("u"), l = params.get("l") || "onboarding", t = params.get("t");
    if (!u || !t) {
      setState({ loading: false, ok: false, msg: "Invalid unsubscribe link." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/unsubscribe?u=${encodeURIComponent(u)}&l=${encodeURIComponent(l)}&t=${encodeURIComponent(t)}`);
        setState({ loading: false, ok: true, msg: data.message || "You're unsubscribed.", email: data.email || "", list: data.list || l });
      } catch (e) {
        setState({ loading: false, ok: false, msg: e.response?.data?.detail || "Could not process unsubscribe." });
      }
    })();
  }, [params]);

  return (
    <div className="dark-theme min-h-screen flex flex-col" data-testid="unsubscribe-page">
      <header className="py-8 px-6">
        <BrandMark />
      </header>
      <main className="flex-1 flex items-center justify-center px-6">
        <div className="parchment p-10 max-w-md w-full text-center">
          {state.loading ? (
            <div className="font-serif text-2xl text-foreground/60">Processing…</div>
          ) : state.ok ? (
            <>
              <CheckCircle2 size={48} strokeWidth={1.25} className="mx-auto text-secondary mb-4" />
              <div className="label-mystic mb-2">Unsubscribed</div>
              <h1 className="font-serif text-3xl mb-3" data-testid="unsubscribe-success">{state.msg}</h1>
              {state.email && (
                <p className="text-sm text-foreground/65">
                  {state.email} will no longer receive {state.list === "all" ? "any Cauldron emails" : `${state.list} emails`}.
                </p>
              )}
              <p className="text-xs text-foreground/45 mt-6">
                Changed your mind? Sign in and update your preferences anytime under <strong>Notifications</strong>.
              </p>
            </>
          ) : (
            <>
              <AlertCircle size={48} strokeWidth={1.25} className="mx-auto text-destructive mb-4" />
              <div className="label-mystic mb-2 text-destructive">Couldn't unsubscribe</div>
              <h1 className="font-serif text-2xl mb-3" data-testid="unsubscribe-error">{state.msg}</h1>
              <p className="text-xs text-foreground/45 mt-4">
                The link may have expired. Please reply to any Cauldron email and we'll unsubscribe you manually.
              </p>
            </>
          )}
          <div className="mt-8">
            <Link to="/" className="text-xs uppercase tracking-wider text-secondary hover:text-secondary/80" data-testid="unsubscribe-home-link">
              Return to Cauldron →
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
